export const DEFAULT_FPS = 60;

/**
 * Snap a measured or hinted rate to 30 / 60 / 120 for HTML5 frame stepping.
 */
export function snapPlayerFps(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 8) return null;
  if (n >= 90) return 120;
  if (n >= 48) return 60;
  if (n >= 20) return 30;
  return null;
}

function median(values) {
  if (!values?.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
}

/**
 * Instantaneous fps from consecutive presented-frame mediaTime deltas.
 * Prefer this over totalVideoFrames/duration (that under-reads mid-clip and
 * often snaps 60fps sources to 30).
 */
export function measureFpsFromMediaTimeDeltas(deltasSec) {
  const rates = (Array.isArray(deltasSec) ? deltasSec : [])
    .map((dt) => Number(dt))
    .filter((dt) => Number.isFinite(dt) && dt > 0.002 && dt < 0.12)
    .map((dt) => 1 / dt);
  const med = median(rates);
  if (!Number.isFinite(med)) return null;
  return snapPlayerFps(med) || (med > 1 && med < 240 ? med : null);
}

/**
 * Best-effort frame duration for single-frame stepping in HTML5 video.
 *
 * Do NOT use totalVideoFrames / duration: while only part of the file is
 * decoded, that ratio is far below the true source rate (e.g. half-decoded
 * 60fps → ~30) and makes stepping / overlays feel locked to 30fps.
 */
export function getVideoFrameDurationSeconds(video, { fallbackFps = DEFAULT_FPS } = {}) {
  const fallback = snapPlayerFps(fallbackFps) || DEFAULT_FPS;
  if (!video) return 1 / fallback;

  const quality = video.getVideoPlaybackQuality?.();
  const elapsed = Number(video.currentTime);
  if (
    quality?.totalVideoFrames > 24 &&
    Number.isFinite(elapsed) &&
    elapsed > 0.75
  ) {
    const measured = quality.totalVideoFrames / elapsed;
    const snapped = snapPlayerFps(measured);
    if (snapped) return 1 / snapped;
    if (measured > 1 && measured < 240) return 1 / measured;
  }

  return 1 / fallback;
}

/**
 * Sample source fps from presented frames via requestVideoFrameCallback.
 * Resolves with snapped 30/60/120 (or raw) once enough deltas are collected.
 */
export function probeVideoSourceFps(
  video,
  { maxSamples = 20, timeoutMs = 2000, fallbackFps = DEFAULT_FPS } = {}
) {
  const fallback = snapPlayerFps(fallbackFps) || DEFAULT_FPS;
  if (!video) return Promise.resolve(fallback);

  if (typeof video.requestVideoFrameCallback !== "function") {
    return Promise.resolve(
      snapPlayerFps(1 / getVideoFrameDurationSeconds(video, { fallbackFps })) ||
        fallback
    );
  }

  return new Promise((resolve) => {
    const deltas = [];
    let lastMediaTime = null;
    let handle = null;
    let settled = false;

    const finish = (fps) => {
      if (settled) return;
      settled = true;
      if (handle != null && typeof video.cancelVideoFrameCallback === "function") {
        try {
          video.cancelVideoFrameCallback(handle);
        } catch {
          /* ignore */
        }
      }
      window.clearTimeout(timer);
      resolve(snapPlayerFps(fps) || fps || fallback);
    };

    const timer = window.setTimeout(() => {
      finish(measureFpsFromMediaTimeDeltas(deltas) || fallback);
    }, timeoutMs);

    const onFrame = (_now, meta) => {
      if (settled) return;
      const mediaTime = Number(meta?.mediaTime);
      if (Number.isFinite(mediaTime) && lastMediaTime != null) {
        const dt = mediaTime - lastMediaTime;
        if (dt > 0.002 && dt < 0.12) deltas.push(dt);
      }
      if (Number.isFinite(mediaTime)) lastMediaTime = mediaTime;

      const measured = measureFpsFromMediaTimeDeltas(deltas);
      if (deltas.length >= maxSamples && measured) {
        finish(measured);
        return;
      }
      handle = video.requestVideoFrameCallback(onFrame);
    };

    handle = video.requestVideoFrameCallback(onFrame);
  });
}

/**
 * Call `onFrame(mediaTimeSec)` once per presented video frame while playing.
 * Falls back to requestAnimationFrame when rVFC is unavailable.
 * Returns an unsubscribe function.
 */
export function subscribePresentedVideoTime(video, onFrame) {
  if (!video || typeof onFrame !== "function") return () => {};

  let stopped = false;
  let rvfcHandle = null;
  let rafHandle = 0;

  const stop = () => {
    stopped = true;
    if (
      rvfcHandle != null &&
      typeof video.cancelVideoFrameCallback === "function"
    ) {
      try {
        video.cancelVideoFrameCallback(rvfcHandle);
      } catch {
        /* ignore */
      }
    }
    if (rafHandle) cancelAnimationFrame(rafHandle);
  };

  if (typeof video.requestVideoFrameCallback === "function") {
    const tick = (_now, meta) => {
      if (stopped) return;
      const t = Number(meta?.mediaTime);
      onFrame(Number.isFinite(t) ? t : video.currentTime || 0);
      rvfcHandle = video.requestVideoFrameCallback(tick);
    };
    rvfcHandle = video.requestVideoFrameCallback(tick);
    return stop;
  }

  const tickRaf = () => {
    if (stopped) return;
    onFrame(video.currentTime || 0);
    rafHandle = requestAnimationFrame(tickRaf);
  };
  rafHandle = requestAnimationFrame(tickRaf);
  return stop;
}
